import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

/**
 * The backend is optional at runtime. With no credentials configured the app
 * still runs fully offline against IndexedDB (useful for `pnpm dev` without a
 * local Supabase, for Storybook and for E2E). Every caller must handle `null`.
 *
 * The client is imported dynamically so supabase-js stays out of the entry
 * chunk; it is a large dependency that offline-only sessions never need.
 *
 * It is typed from `database.types.ts`, which `pnpm supabase:types` regenerates
 * from the migrations, so a column renamed in SQL breaks the build here rather
 * than at runtime.
 */

export type Db = SupabaseClient<Database>;

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let client: Db | null = null;
let loading: Promise<Db> | null = null;

export function isBackendConfigured(): boolean {
  return (
    typeof url === 'string' && url.length > 0 && typeof anonKey === 'string' && anonKey.length > 0
  );
}

/** Resolves to the client, or null when no backend is configured. */
export async function getSupabase(): Promise<Db | null> {
  if (!isBackendConfigured()) return null;
  if (client !== null) return client;

  loading ??= import('@supabase/supabase-js').then(({ createClient }) =>
    createClient<Database>(url as string, anonKey as string, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: 'audigoal.auth',
      },
    }),
  );

  client = await loading;
  return client;
}
